import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { Invoice } from '../types';

interface UseInvoicesOptions {
  contractId?: string;
  customerId?: number;
  autoFetch?: boolean;
  contractIds?: string[]; // For batch fetching
}

interface UseInvoicesResult {
  invoices: Invoice[];
  loading: boolean;
  error: string | null;
  fetchInvoices: () => Promise<void>;
  getInvoicesByContract: (contractId: string) => Invoice[];
  getInvoicesByCustomer: (customerId: number) => Invoice[];
  fetchInvoicesForContracts: (contractIds: string[]) => Promise<void>;
}

// Cache for invoices to prevent redundant API calls
const invoiceCache = new Map<string, Invoice[]>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let lastFetchTime = 0;

export const useInvoices = (options: UseInvoicesOptions = {}): UseInvoicesResult => {
  const { contractId, customerId, autoFetch = true, contractIds } = options;
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvoices = useCallback(async () => {
    // Check if we have recent cached data
    const now = Date.now();
    if (invoiceCache.has('all') && (now - lastFetchTime) < CACHE_DURATION) {
      setInvoices(invoiceCache.get('all') || []);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Fetch all invoices in a single request
      const response = await apiService.getInvoices(1, 1000);
      const allInvoices = response.items; // Extract items array from paginated response
      
      // Cache the results
      invoiceCache.set('all', allInvoices);
      lastFetchTime = now;
      
      // Group invoices by contract for quick lookup
      const invoicesByContract = new Map<string, Invoice[]>();
      const invoicesByCustomer = new Map<number, Invoice[]>();
      
      allInvoices.forEach((invoice: Invoice) => {
        // Group by contract
        if (invoice.contract_id) {
          if (!invoicesByContract.has(invoice.contract_id)) {
            invoicesByContract.set(invoice.contract_id, []);
          }
          invoicesByContract.get(invoice.contract_id)!.push(invoice);
        }
        
        // Group by customer
        if (invoice.customer_id) {
          if (!invoicesByCustomer.has(invoice.customer_id)) {
            invoicesByCustomer.set(invoice.customer_id, []);
          }
          invoicesByCustomer.get(invoice.customer_id)!.push(invoice);
        }
      });
      
      // Cache grouped results
      invoicesByContract.forEach((invoices, contractId) => {
        invoiceCache.set(`contract:${contractId}`, invoices);
      });
      
      invoicesByCustomer.forEach((invoices, customerId) => {
        invoiceCache.set(`customer:${customerId}`, invoices);
      });
      
      // Filter based on options
      let filteredInvoices = allInvoices;
      if (contractId) {
        filteredInvoices = invoicesByContract.get(contractId) || [];
      } else if (customerId) {
        filteredInvoices = invoicesByCustomer.get(customerId) || [];
      }
      
      setInvoices(filteredInvoices);
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
      setError(err.message || 'Failed to fetch invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [contractId, customerId]);

  // Get invoices for a specific contract from cache
  const getInvoicesByContract = useCallback((contractId: string): Invoice[] => {
    const cached = invoiceCache.get(`contract:${contractId}`);
    if (cached) return cached;
    
    // If not in cache, filter from all invoices
    const allInvoices = invoiceCache.get('all') || [];
    return allInvoices.filter(inv => inv.contract_id === contractId);
  }, []);

  // Get invoices for a specific customer from cache
  const getInvoicesByCustomer = useCallback((customerId: number): Invoice[] => {
    const cached = invoiceCache.get(`customer:${customerId}`);
    if (cached) return cached;
    
    // If not in cache, filter from all invoices
    const allInvoices = invoiceCache.get('all') || [];
    return allInvoices.filter(inv => inv.customer_id === customerId);
  }, []);

  // Fetch invoices for multiple contracts in a single batch request
  const fetchInvoicesForContracts = useCallback(async (contractIds: string[]) => {
    if (contractIds.length === 0) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Check cache first - if all contracts are cached and fresh, use cache
      const now = Date.now();
      const allCached = contractIds.every(id => {
        const cached = invoiceCache.get(`contract:${id}`);
        return cached && (now - lastFetchTime) < CACHE_DURATION;
      });
      
      if (allCached) {
        // Combine all cached invoices
        const allInvoices: Invoice[] = [];
        contractIds.forEach(id => {
          const cached = invoiceCache.get(`contract:${id}`);
          if (cached) allInvoices.push(...cached);
        });
        setInvoices(allInvoices);
        return;
      }
      
      // Make batch API call
      const batchResult = await apiService.getInvoicesByContractsBatch(contractIds);
      
      // Cache the results
      const allInvoices: Invoice[] = [];
      Object.entries(batchResult).forEach(([contractId, invoices]) => {
        invoiceCache.set(`contract:${contractId}`, invoices as Invoice[]);
        allInvoices.push(...(invoices as Invoice[]));
      });
      
      lastFetchTime = now;
      setInvoices(allInvoices);
    } catch (err: any) {
      console.error('Error fetching invoices for contracts:', err);
      setError(err.message || 'Failed to fetch invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      if (contractIds && contractIds.length > 0) {
        fetchInvoicesForContracts(contractIds);
      } else {
        fetchInvoices();
      }
    }
  }, [autoFetch, fetchInvoices, fetchInvoicesForContracts, contractIds]);

  return {
    invoices,
    loading,
    error,
    fetchInvoices,
    getInvoicesByContract,
    getInvoicesByCustomer,
    fetchInvoicesForContracts
  };
};

// Export a function to clear the cache when needed
export const clearInvoiceCache = () => {
  invoiceCache.clear();
  lastFetchTime = 0;
};